// ===========================================
// WHOLESALE WORKFLOW — Tassilo's 5-step flow
// ===========================================
// Open Orders → In Transit (picked up) → Delivered → Invoice Sent → Payment
// Received, plus Direct. Moving a PO forward opens a small gated popup asking
// for exactly the data that stage creates (pickup date/freight, delivery date,
// invoice number, amount + method) — so a PO can't advance without its record
// being complete. His Sep 4 feedback, verbatim.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Pencil, Trash2, Check, X, ChevronDown, Paperclip, Loader2, AlertCircle, Camera, Download, Filter } from 'lucide-react';
import clsx from 'clsx';
import { formatMoney, formatByCurrency } from '../lib/money';
import { wholesaleApi, inventoryApi } from '../services/api';
import { skuLabel } from './ScanPOModal';

// Money speaks its own currency — pass the order/invoice currencyCode and
// $1,240 / €1,240 / 8.450 kr come out right; omitted = USD as before.
const fmt = (c: number, cur?: string | null) => formatMoney(c, cur || 'USD');
const fmtSigned = (c: number | null | undefined) => c == null ? '—' : c === 0 ? '—' : `${c < 0 ? '−' : '+'}$${Math.abs(c / 100).toFixed(2)}`;
const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '—');
const dateInput = (v?: string | null) => (v ? new Date(v).toISOString().slice(0, 10) : '');
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const monthName = (ym: string) => { const i = parseInt(ym.slice(5), 10) - 1; return ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'][i] || '—'; };

type StepKey = 'open' | 'transit' | 'delivered' | 'sales' | 'closed' | 'direct';
const STEPS: Array<{ key: StepKey; label: string }> = [
  { key: 'open', label: 'Open Orders' },
  { key: 'transit', label: 'In Transit' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'sales', label: 'Invoice Sent' },
  { key: 'closed', label: 'Payment Received' },
  { key: 'direct', label: 'Direct' },
];

function stepOf(o: any): StepKey {
  if (o.paymentReceived) return 'closed';
  if (o.invoiceSent) return 'sales';
  if (o.freightDelivered) return 'delivered';
  if (o.pickedUp) return 'transit';
  return 'open';
}
const orderMonth = (o: any): string => String(o.deliveryDate || o.fulfillmentDate || o.createdAt || '').slice(0, 7);

// Calm pills, straight from his mock
const Pill = ({ tone, children, onClick, title }: { tone: 'green' | 'red' | 'grey'; children: any; onClick?: () => void; title?: string }) => (
  <button onClick={onClick} disabled={!onClick} title={title}
    className={clsx('text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap',
      tone === 'green' && 'bg-emerald-100 text-emerald-800',
      tone === 'red' && 'bg-rose-100 text-rose-700',
      tone === 'grey' && 'bg-slate-100 text-slate-500',
      onClick && 'cursor-pointer hover:opacity-80')}>
    {children}
  </button>
);

export default function POPipeline({ orders, invoices = [], menuItems = [], say, initialQuery, onOpenInvoice, onAdd, onScan, onImport, onSyncFaire }: {
  orders: any[];
  invoices?: any[];
  menuItems?: any[];
  say: (tone: 'success' | 'error', text: string) => void;
  initialQuery?: string;
  onOpenInvoice?: (invoiceNumber: string) => void;
  onAdd?: () => void;
  onScan?: () => void;
  onImport?: () => void;
  onSyncFaire?: () => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<StepKey>('open');
  const year = String(new Date().getFullYear());
  const [month, setMonth] = useState<string>('all'); // 'all' | 'YYYY-MM'
  const [q, setQ] = useState(initialQuery || '');
  const [showSearch, setShowSearch] = useState(!!initialQuery);
  const [detailFor, setDetailFor] = useState<any | null>(null); // PO detail popup (his page 3)
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [showPlus, setShowPlus] = useState(false);
  const [invoiceFor, setInvoiceFor] = useState<any | null>(null);
  const [invForm, setInvForm] = useState({ invoiceNumber: '', to: '', bodyText: '', dueAt: '', sentAt: '' });
  const [receiveFor, setReceiveFor] = useState<any | null>(null);
  const [recvForm, setRecvForm] = useState({ amount: '', reason: '', note: '', receivedAt: '', method: 'ach' });
  // Gated stage moves — the popup collects exactly what the stage creates.
  const [pickupFor, setPickupFor] = useState<any | null>(null);
  const [pickupForm, setPickupForm] = useState({ pickupDate: '', broker: '', freightDollars: '', billedOnward: false });
  const [deliverFor, setDeliverFor] = useState<any | null>(null);
  const [deliverDate, setDeliverDate] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showConsign, setShowConsign] = useState(false);
  const [preview, setPreview] = useState<{ name: string; url: string; isPdf: boolean; isImage: boolean } | null>(null);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [custFilter, setCustFilter] = useState('');
  const [showCustMenu, setShowCustMenu] = useState(false);

  const invByOrder = new Map<string, any>((invoices as any[]).filter((i) => i.orderId && i.status !== 'void').map((i) => [i.orderId, i]));

  // Inventory costs for the per-delivery margin in the PO detail — only
  // fetched once a detail is open. Matched by exact product name or SKU;
  // unmatched lines leave the margin blank rather than half-true.
  const { data: invItems = [] } = useQuery({
    queryKey: ['inventory-for-drop-margin'],
    queryFn: async () => (await inventoryApi.getItems()).data?.data || [],
    staleTime: 60_000,
    enabled: !!detailFor,
  });
  const dropEconomics = (o: any) => {
    const normTxt = (s: unknown) => String(s || '').trim().toLowerCase();
    const byName = new Map<string, number>();
    const bySku = new Map<string, number>();
    for (const it of invItems as any[]) {
      byName.set(normTxt(it.name), Math.round((it.unitCost || 0) * 100));
      if (it.sku) bySku.set(normTxt(it.sku), Math.round((it.unitCost || 0) * 100));
    }
    let itemCost = 0; let unmatched = 0;
    for (const it of (o.items || []) as any[]) {
      const qty = parseFloat(String(it.quantity)) || 0;
      const name = normTxt(it.name);
      const cost = byName.get(name)
        ?? [...bySku.entries()].find(([sku]) => sku && name.includes(sku))?.[1];
      if (cost == null) unmatched += 1;
      else itemCost += Math.round(qty * cost);
    }
    const delivery = o.deliveryCostCents;
    const margin = unmatched === 0 && delivery != null ? (o.totalCents || 0) - itemCost - delivery : null;
    return { itemCost: unmatched === 0 ? itemCost : null, unmatched, delivery, margin };
  };

  const refresh = () => { qc.invalidateQueries({ queryKey: ['wholesale-orders'] }); qc.invalidateQueries({ queryKey: ['wholesale-invoices'] }); };
  const update = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Record<string, unknown> }) => wholesaleApi.setStage(id, updates),
    onSuccess: refresh,
    onError: () => say('error', "Couldn't update the PO."),
  });
  const remove = useMutation({
    mutationFn: (id: string) => wholesaleApi.deleteOrder(id),
    onSuccess: () => { refresh(); setConfirmDelete(null); say('success', 'Order deleted.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't delete the order."),
  });
  const sendInvoice = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => wholesaleApi.sendOrderInvoice(id, body),
    onSuccess: (res) => {
      const d = res.data?.data || {};
      refresh(); setInvoiceFor(null);
      say('success', d.marked ? `Marked as invoiced (${d.invoiceNumber}).` : `Invoice ${d.invoiceNumber} sent to ${(d.sentTo || []).join(', ')}.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't send the invoice."),
  });
  const receive = useMutation({
    mutationFn: async ({ order, cents, reason, note, receivedAt, method }: { order: any; cents: number; reason: string; note?: string; receivedAt?: string; method?: string }) => {
      const inv = invByOrder.get(order.id);
      if (inv) {
        const shortfall = Math.max(0, (inv.totalCents - inv.amountPaidCents) - cents);
        await wholesaleApi.recordPayment(inv.id, cents, shortfall > 0 && reason ? { amountCents: shortfall, reasonCode: reason, notes: note || undefined } : undefined, { receivedAt, paymentMethod: method });
      }
      await wholesaleApi.setStage(order.id, { paymentReceived: true });
    },
    onSuccess: () => { refresh(); setReceiveFor(null); setRecvForm({ amount: '', reason: '', note: '', receivedAt: '', method: 'ach' }); say('success', 'Payment recorded — PO closed.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't record the payment."),
  });
  // Gated stage moves — pickup gates entry to In Transit; delivery date gates Delivered.
  const confirmPickup = () => {
    if (!pickupFor || !pickupForm.pickupDate || !pickupForm.broker.trim() || pickupForm.freightDollars === '') return;
    const dollars = Math.abs(parseFloat(pickupForm.freightDollars) || 0);
    update.mutate({ id: pickupFor.id, updates: {
      pickedUp: true, freightScheduled: true, pickupDate: pickupForm.pickupDate,
      broker: pickupForm.broker.trim(),
      // signed convention: negative = we pay this freight; positive = billed onward
      freightCents: Math.round(dollars * 100) * (pickupForm.billedOnward ? 1 : -1),
    } });
    setPickupFor(null);
  };
  const confirmDeliver = () => {
    if (!deliverFor || !deliverDate) return;
    update.mutate({ id: deliverFor.id, updates: { freightDelivered: true, deliveryDate: deliverDate } });
    setDeliverFor(null);
  };
  const upload = useMutation({
    mutationFn: ({ id, file, kind }: { id: string; file: File; kind: 'po' | 'bol' }) => wholesaleApi.uploadOrderAttachment(id, file, kind),
    onSuccess: () => { refresh(); say('success', 'File attached.'); },
    onError: () => say('error', "The file couldn't be attached."),
  });
  const removeFile = useMutation({
    mutationFn: ({ id, fileId }: { id: string; fileId: string }) => wholesaleApi.deleteOrderAttachment(id, fileId),
    onSuccess: refresh,
  });
  const openFile = async (orderId: string, f: any) => {
    const res = await wholesaleApi.downloadOrderAttachment(orderId, f.id);
    const isPdf = /pdf/i.test(res.data.type || '') || /\.pdf$/i.test(f.name || '');
    const isImage = /^image\//i.test(res.data.type || '') || /\.(png|jpe?g|gif|webp)$/i.test(f.name || '');
    // browsers only render a blob iframe as a PDF when the blob says so
    const blob = isPdf ? new Blob([res.data], { type: 'application/pdf' }) : res.data;
    setPreview({ name: f.name, url: URL.createObjectURL(blob), isPdf, isImage });
  };
  const closePreview = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };
  const downloadPreview = () => {
    if (!preview) return;
    const a = document.createElement('a'); a.href = preview.url; a.download = preview.name; a.click();
  };

  // ---- bucketing
  const needle = q.trim().toLowerCase();
  const match = (o: any) => !needle || [o.poNumber, o.orderNumber, o.customer?.name, o.broker, o.notes].some((v) => String(v || '').toLowerCase().includes(needle));
  const isDirectDelivery = (o: any) => !o.poNumber && /direct delivery/i.test(o.notes || '');
  const consignment = orders.filter((o) => o.status !== 'cancelled' && o.onConsignment && !o.paymentReceived);
  const active = orders.filter((o) => o.status !== 'cancelled' && !o.onConsignment && !isDirectDelivery(o) && match(o));
  const directs = orders.filter((o) => o.status !== 'cancelled' && isDirectDelivery(o) && match(o));
  const byStep = new Map<StepKey, any[]>(STEPS.map((s) => [s.key, []]));
  for (const o of active) byStep.get(stepOf(o))!.push(o);
  byStep.set('direct', directs);

  const cases = (o: any) => (o.items || []).reduce((s: number, it: any) => s + (parseFloat(String(it.quantity)) || 0), 0);

  const stepOrders = byStep.get(step)!;
  const custNames = [...new Set(stepOrders.map((o) => o.customer?.name).filter(Boolean))].sort() as string[];
  let visible = (step === 'open' || month === 'all') ? stepOrders : stepOrders.filter((o) => orderMonth(o) === month);
  if (custFilter) visible = visible.filter((o) => (o.customer?.name || '') === custFilter);

  // header sorting — one key per column label
  const sortKeys: Record<string, (o: any) => any> = {
    Month: (o) => String(o.fulfillmentDate || ''), Date: (o) => String(o.fulfillmentDate || ''),
    'PO Number': (o) => String(o.poNumber || o.orderNumber || ''), Order: (o) => String(o.orderNumber || ''),
    Distributor: (o) => String(o.customer?.name || ''), Customer: (o) => String(o.customer?.name || ''),
    Cases: cases, Value: (o) => o.totalCents || 0,
    Status: (o) => (o.fulfillable === false ? 0 : 1),
    'Freight Scheduled?': (o) => (o.freightScheduled ? 1 : 0),
    Freight: (o) => String(o.broker || ''), Costs: (o) => o.freightCents ?? 0,
    'Expected Ship': (o) => String(o.shipDate || ''),
    'Picked Up': (o) => String(o.pickupDate || ''),
    'Delivered On': (o) => String(o.deliveryDate || o.fulfillmentDate || ''),
    Method: (o) => String(invByOrder.get(o.id)?.paymentMethod || ''),
    Delivery: (o) => String(o.deliveryDate || ''),
    Delivered: (o) => (step === 'direct' ? (o.freightDelivered ? 1 : 0) : String(o.deliveryDate || o.fulfillmentDate || '')),
    Invoice: (o) => (o.invoiceSent ? 1 : 0), Invoiced: (o) => (o.invoiceSent ? 1 : 0), Paid: (o) => (o.paymentReceived ? 1 : 0),
    Number: (o) => String(invByOrder.get(o.id)?.invoiceNumber || ''),
    Sent: (o) => String(invByOrder.get(o.id)?.sentAt || ''),
    Due: (o) => String(invByOrder.get(o.id)?.dueAt || ''),
    Received: (o) => String(invByOrder.get(o.id)?.paidAt || ''),
    Deductions: (o) => { const i = invByOrder.get(o.id); return i ? i.totalCents - i.amountPaidCents : 0; },
  };
  // Profit per order: total minus recipe-linked COGS. Only shown when every
  // line has a costed product — a partial guess would read as a real number.
  const menuById = new Map((menuItems as any[]).map((m: any) => [m.id, m]));
  const orderProfit = (o: any): number | null => {
    const items = o.items || []; if (!items.length || !o.totalCents) return null;
    let cogs = 0;
    for (const it of items) {
      const c = menuById.get(it.menuItemId)?.costPriceCents;
      if (!c) return null;
      cogs += c * (parseFloat(String(it.quantity)) || 0);
    }
    return Math.round(((o.totalCents - cogs) / o.totalCents) * 100);
  };
  const keyOf = sortCol ? sortKeys[sortCol] : null;
  visible = [...visible];
  if (keyOf) visible.sort((a, b) => { const va = keyOf(a), vb = keyOf(b); const c = va < vb ? -1 : va > vb ? 1 : 0; return sortDir === 'asc' ? c : -c; });
  else visible.sort((a, b) => String(b.fulfillmentDate || '').localeCompare(String(a.fulfillmentDate || '')));
  const toggleSort = (c: string) => {
    if (sortCol !== c) { setSortCol(c); setSortDir('desc'); }
    else if (sortDir === 'desc') setSortDir('asc');
    else { setSortCol(null); setSortDir('desc'); } // third tap: back to newest-first
  };
  const input = 'w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5 focus:ring-2 focus:ring-primary-400 focus:border-primary-400';

  const beginEdit = (o: any) => {
    setEditId(editId === o.id ? null : o.id);
    setForm({
      poNumber: o.poNumber || '', broker: o.broker || '', shipFrom: o.shipFrom || '', warehouse: o.warehouse || '',
      lotCode: o.lotCode || '', shipDate: dateInput(o.shipDate), deliveryDate: dateInput(o.deliveryDate),
      fulfillmentDate: dateInput(o.fulfillmentDate),
      freightDollars: o.freightCents != null ? (o.freightCents / 100).toString() : '', onConsignment: !!o.onConsignment,
      deliveryDollars: o.deliveryCostCents != null ? (o.deliveryCostCents / 100).toString() : '',
      items: (o.items || []).map((it: any) => ({ menuItemId: it.menuItemId, name: it.name, quantity: parseFloat(String(it.quantity)) || 0, unitDollars: ((it.unitPriceCents || 0) / 100).toFixed(2), lotCode: it.lotCode || '' })),
    });
  };
  const saveEdit = (id: string) => {
    update.mutate({ id, updates: {
      poNumber: form.poNumber || null, broker: form.broker || null, shipFrom: form.shipFrom || null,
      warehouse: form.warehouse || null, lotCode: form.lotCode || null,
      shipDate: form.shipDate || null, deliveryDate: form.deliveryDate || null,
      fulfillmentDate: form.fulfillmentDate || null,
      freightCents: form.freightDollars === '' ? null : Math.round(parseFloat(form.freightDollars) * 100),
      deliveryCostCents: form.deliveryDollars === '' ? null : Math.round((parseFloat(form.deliveryDollars) || 0) * 100),
      onConsignment: !!form.onConsignment,
      items: (form.items || []).filter((l: any) => l.menuItemId && l.quantity > 0).map((l: any) => ({ menuItemId: l.menuItemId, quantity: l.quantity, unitPriceCents: Math.round(parseFloat(l.unitDollars || '0') * 100) || undefined, lotCode: l.lotCode || undefined })),
    } });
    setEditId(null);
  };

  const openInvoiceModal = (o: any) => {
    const c = o.customer || {};
    setInvoiceFor(o);
    setInvForm({
      invoiceNumber: invByOrder.get(o.id)?.invoiceNumber || '',
      to: c.invoiceEmail || c.email || '',
      bodyText: (c.invoiceText || 'Hi, please see attached invoice related to Purchase Order: XXX.').replace(/XXX/g, o.poNumber || o.orderNumber),
      dueAt: '',
      sentAt: '',
    });
  };

  // Column sets per step — his mock + Sep 4 feedback (freight details live in
  // the pickup popup now; Open shows what's about to ship instead)
  const COLS: Record<StepKey, string[]> = {
    open: ['Month', 'Date', 'PO Number', 'Distributor', 'Cases', 'Value', 'Status', 'Freight Scheduled?', 'Expected Ship', ''],
    transit: ['Month', 'Date', 'PO Number', 'Distributor', 'Cases', 'Value', 'Picked Up', 'Freight', 'Costs', ''],
    delivered: ['Month', 'Date', 'PO Number', 'Distributor', 'Cases', 'Value', 'Delivered On', 'Freight', 'Costs', ''],
    sales: ['Month', 'Date', 'PO Number', 'Distributor', 'Cases', 'Value', 'Status', 'Invoice', 'Number', 'Sent', 'Due', ''],
    closed: ['Month', 'Date', 'PO Number', 'Distributor', 'Cases', 'Value', 'Received', 'Number', 'Sent', 'Received', 'Method', 'Deductions', ''],
    direct: ['Month', 'Date', 'Order', 'Customer', 'Cases', 'Value', 'Delivered', 'Invoiced', 'Paid', ''],
  };

  const rowCells = (o: any) => {
    const inv = invByOrder.get(o.id);
    const base = [
      <td key="m" className="px-3 py-3 text-slate-500">{orderMonth(o) ? monthName(orderMonth(o)) : '—'}</td>,
      <td key="d" className="px-3 py-3 text-slate-500">{fmtDate(o.fulfillmentDate)}</td>,
      <td key="po" className="px-3 py-3 font-semibold text-slate-900 whitespace-nowrap">
        <button onClick={() => setDetailFor(o)} className="flex items-center gap-1.5 hover:text-primary-700" title="SKU breakdown & documents">
          {o.poNumber || o.orderNumber}
          {(o.attachments || []).length > 0 && <Paperclip className="w-3 h-3 text-slate-300" />}
        </button>
      </td>,
      <td key="c" className="px-3 py-3 text-slate-700">{o.customer?.name}</td>,
      <td key="cs" className="px-3 py-3 text-right text-slate-700">{Math.round(cases(o)).toLocaleString('en-US')}</td>,
      <td key="v" className="px-3 py-3 text-right whitespace-nowrap"><span className="text-slate-900 font-medium">{fmt(o.totalCents, o.currencyCode)}</span>{(() => { const m = orderProfit(o); return m == null ? null : <div className="text-[10px] text-emerald-600" title="After recipe costs — from your linked batch recipes">{m}% margin</div>; })()}</td>,
    ];
    const icons = (
      <td key="i" className="px-2 py-3 text-right whitespace-nowrap">
        <button onClick={() => beginEdit(o)} className={clsx('p-1.5 rounded-lg hover:bg-slate-100', editId === o.id ? 'text-primary-600' : 'text-slate-300 hover:text-slate-500')}><Pencil className="w-3.5 h-3.5" /></button>
        <button onClick={() => setConfirmDelete(o.id)} className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
      </td>
    );
    if (step === 'open') return [...base,
      <td key="st" className="px-3 py-3 text-center">
        <Pill tone={o.fulfillable === false ? 'red' : 'green'} title="Tap to toggle — Unfulfilled = the stock isn't there"
          onClick={() => update.mutate({ id: o.id, updates: { fulfillable: o.fulfillable === false ? true : false } })}>
          {o.fulfillable === false ? 'Unfulfilled' : 'Confirmed'}
        </Pill>
      </td>,
      <td key="fs" className="px-3 py-3 text-center">
        <Pill tone={o.freightScheduled ? 'green' : 'red'} title="Tap to toggle"
          onClick={() => update.mutate({ id: o.id, updates: { freightScheduled: !o.freightScheduled } })}>
          {o.freightScheduled ? 'Yes' : 'No'}
        </Pill>
      </td>,
      <td key="sd" className="px-3 py-3 text-slate-500">{fmtDate(o.shipDate)}</td>,
      <td key="a" className="px-2 py-3 text-right">
        <button onClick={() => { setPickupFor(o); setPickupForm({ pickupDate: '', broker: o.broker || '', freightDollars: o.freightCents != null ? (Math.abs(o.freightCents) / 100).toString() : '', billedOnward: (o.freightCents || 0) > 0 || !!o.customer?.paysFreight }); }}
          className="text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 px-3.5 py-1.5 rounded-full whitespace-nowrap">Picked Up</button>
      </td>,
      icons];
    if (step === 'transit') return [...base,
      <td key="pu" className="px-3 py-3 text-center">
        <Pill tone="green" title={`Picked up ${fmtDate(o.pickupDate)} — tap to undo, back to Open`}
          onClick={() => update.mutate({ id: o.id, updates: { pickedUp: false } })}>
          {fmtDate(o.pickupDate)}
        </Pill>
      </td>,
      <td key="fp" className="px-3 py-3 text-slate-600">{o.broker || '—'}</td>,
      <td key="fc" className="px-3 py-3 text-right text-slate-600">{fmtSigned(o.freightCents)}</td>,
      <td key="a" className="px-2 py-3 text-right">
        <button onClick={() => { setDeliverFor(o); setDeliverDate(''); }}
          className="text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 px-3.5 py-1.5 rounded-full">Delivered</button>
      </td>,
      icons];
    if (step === 'delivered') return [...base,
      <td key="dd" className="px-3 py-3 text-center">
        <Pill tone="green" title="Tap to undo — back to In Transit"
          onClick={() => update.mutate({ id: o.id, updates: { freightDelivered: false } })}>
          {fmtDate(o.deliveryDate || o.fulfillmentDate)}
        </Pill>
      </td>,
      <td key="fp" className="px-3 py-3 text-slate-600">{o.broker || '—'}</td>,
      <td key="fc" className="px-3 py-3 text-right text-slate-600">{fmtSigned(o.freightCents)}</td>,
      <td key="a" className="px-2 py-3 text-right">
        <button onClick={() => openInvoiceModal(o)} className="text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 px-3.5 py-1.5 rounded-full whitespace-nowrap">Send Invoice</button>
      </td>,
      icons];
    if (step === 'sales') return [...base,
      <td key="st" className="px-3 py-3 text-center"><Pill tone="green">Delivered</Pill></td>,
      <td key="iv" className="px-3 py-3 text-center"><Pill tone="green">Sent</Pill></td>,
      <td key="n" className="px-3 py-3 text-slate-700">
        {inv ? <button onClick={() => onOpenInvoice?.(inv.invoiceNumber)} className="text-primary-600">{inv.invoiceNumber}</button> : '—'}
      </td>,
      <td key="s" className="px-3 py-3 text-slate-500">{fmtDate(inv?.sentAt)}</td>,
      <td key="du" className="px-3 py-3 text-slate-500">{fmtDate(inv?.dueAt)}</td>,
      <td key="a" className="px-2 py-3 text-right">
        <button onClick={() => { setReceiveFor(o); setRecvForm({ amount: inv ? ((inv.totalCents - inv.amountPaidCents) / 100).toFixed(2) : '', reason: '', note: '', receivedAt: '', method: 'ach' }); }}
          className="text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 px-3.5 py-1.5 rounded-full">Received</button>
      </td>,
      icons];
    if (step === 'direct') return [
      <td key="m" className="px-3 py-3 text-slate-500">{orderMonth(o) ? monthName(orderMonth(o)) : '—'}</td>,
      <td key="d" className="px-3 py-3 text-slate-500">{fmtDate(o.fulfillmentDate)}</td>,
      <td key="po" className="px-3 py-3 font-semibold text-slate-900 whitespace-nowrap">
        <button onClick={() => setDetailFor(o)} className="hover:text-primary-700">{o.orderNumber}</button>
      </td>,
      <td key="c" className="px-3 py-3 text-slate-700">{o.customer?.name}</td>,
      <td key="cs" className="px-3 py-3 text-right text-slate-700">{Math.round(cases(o)).toLocaleString('en-US')}</td>,
      <td key="v" className="px-3 py-3 text-right text-slate-900 font-medium">{fmt(o.totalCents, o.currencyCode)}</td>,
      <td key="dv" className="px-3 py-3 text-center"><Pill tone={o.freightDelivered ? 'green' : 'grey'} onClick={() => update.mutate({ id: o.id, updates: { freightDelivered: !o.freightDelivered } })}>{o.freightDelivered ? 'Yes' : '—'}</Pill></td>,
      <td key="iv" className="px-3 py-3 text-center"><Pill tone={o.invoiceSent ? 'green' : 'grey'} onClick={() => update.mutate({ id: o.id, updates: { invoiceSent: !o.invoiceSent } })}>{o.invoiceSent ? 'Yes' : '—'}</Pill></td>,
      <td key="pd" className="px-3 py-3 text-center"><Pill tone={o.paymentReceived ? 'green' : 'grey'} onClick={() => update.mutate({ id: o.id, updates: { paymentReceived: !o.paymentReceived } })}>{o.paymentReceived ? 'Yes' : '—'}</Pill></td>,
      icons];
    // closed
    const deduction = inv ? inv.totalCents - inv.amountPaidCents : 0;
    return [...base,
      <td key="r" className="px-3 py-3 text-center"><Pill tone="green">Yes</Pill></td>,
      <td key="n" className="px-3 py-3 text-slate-700">
        {inv ? <button onClick={() => onOpenInvoice?.(inv.invoiceNumber)} className="text-primary-600">{inv.invoiceNumber}</button> : '—'}
      </td>,
      <td key="s" className="px-3 py-3 text-slate-500">{fmtDate(inv?.sentAt)}</td>,
      <td key="rd" className="px-3 py-3 text-slate-500">{fmtDate(inv?.paidAt)}</td>,
      <td key="pm" className="px-3 py-3 text-slate-500 uppercase text-xs">{inv?.paymentMethod || '—'}</td>,
      <td key="de" className="px-3 py-3 text-right text-slate-600">{deduction > 0 ? `−${fmt(deduction, inv?.currencyCode)}` : '—'}</td>,
      icons];
  };

  const colSpan = COLS[step].length + 1;

  return (
    <div className="space-y-3">
      {/* + · step tabs · search — his top rail */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <button onClick={() => setShowPlus((s) => !s)} className="w-9 h-9 rounded-full bg-slate-800 text-white flex items-center justify-center hover:bg-slate-700"><Plus className="w-4 h-4" /></button>
          {showPlus && (
            <div className="absolute z-20 mt-1 bg-white rounded-xl shadow-lg border border-slate-100 py-1 min-w-[160px]">
              {onScan && <button onClick={() => { setShowPlus(false); onScan(); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"><Camera className="w-4 h-4 text-slate-400" /> Scan a PO</button>}
              {onAdd && <button onClick={() => { setShowPlus(false); onAdd(); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"><Plus className="w-4 h-4 text-slate-400" /> Add by hand</button>}
              {onImport && <button onClick={() => { setShowPlus(false); onImport(); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"><Paperclip className="w-4 h-4 text-slate-400" /> Import spreadsheet</button>}
              {onSyncFaire && <button onClick={() => { setShowPlus(false); onSyncFaire(); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"><Loader2 className="w-4 h-4 text-slate-400" /> Sync Faire</button>}
            </div>
          )}
        </div>
        {STEPS.filter((s) => s.key !== 'direct' || directs.length > 0).map((s) => (
          <button key={s.key} onClick={() => { setStep(s.key); setMonth('all'); }}
            className={clsx('px-4 py-2 rounded-full text-sm font-medium',
              step === s.key ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')}>
            {s.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          {showSearch ? (
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => !q && setShowSearch(false)} placeholder="PO #, account…"
              className="text-sm rounded-xl border border-slate-200 px-3 py-2 w-48 focus:ring-2 focus:ring-primary-400 focus:border-primary-400" />
          ) : (
            <button onClick={() => setShowSearch(true)} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100"><Search className="w-4 h-4" /></button>
          )}
        </div>
      </div>

      {/* Year + JAN–DEC rail (the later steps work month by month) */}
      {step !== 'open' && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button onClick={() => setMonth('all')}
            className={clsx('px-3 py-1.5 rounded-full text-xs font-semibold', month === 'all' ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200')}>{year}</button>
          {MONTHS.map((m, i) => {
            const ym = `${year}-${String(i + 1).padStart(2, '0')}`;
            const has = stepOrders.some((o) => orderMonth(o) === ym);
            return (
              <button key={m} onClick={() => setMonth(month === ym ? 'all' : ym)}
                className={clsx('px-3 py-1.5 rounded-full text-xs font-semibold',
                  month === ym ? 'bg-primary-600 text-white' : has ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-slate-50 text-slate-300')}>
                {m}
              </button>
            );
          })}
        </div>
      )}

      {/* Consignment — quiet amber note */}
      {consignment.length > 0 && step === 'open' && (
        <button onClick={() => setShowConsign((s) => !s)} className="w-full text-left text-xs text-amber-700 bg-amber-50/70 hover:bg-amber-50 rounded-xl px-3 py-2">
          {formatByCurrency(consignment.reduce((m: Record<string, number>, o) => { const c = String(o.currencyCode || 'USD').toUpperCase(); m[c] = (m[c] || 0) + (o.totalCents || 0); return m; }, {}))} on consignment ({Math.round(consignment.reduce((s, o) => s + cases(o), 0)).toLocaleString('en-US')} cases) at {[...new Set(consignment.map((o) => o.customer?.name))].join(', ')} — billed monthly on depletion, not a sale yet. <span className="font-semibold">{showConsign ? 'Hide' : 'Show'} the {consignment.length} shipments</span>
        </button>
      )}
      {showConsign && step === 'open' && (
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-50">
              {consignment.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50/50">
                  <td className="px-3 py-2.5 font-semibold text-slate-900"><button onClick={() => setDetailFor(o)} className="hover:text-primary-700">{o.poNumber || o.orderNumber}</button></td>
                  <td className="px-3 py-2.5 text-slate-700">{o.customer?.name}</td>
                  <td className="px-3 py-2.5 text-right text-slate-700">{Math.round(cases(o)).toLocaleString('en-US')} cases</td>
                  <td className="px-3 py-2.5 text-right text-slate-900 font-medium">{fmt(o.totalCents, o.currencyCode)}</td>
                  <td className="px-3 py-2.5 text-slate-500">{fmtDate(o.fulfillmentDate)}</td>
                  <td className="px-2 py-2.5 text-right"><button onClick={() => beginEdit(o)} className="p-1.5 rounded-lg text-slate-300 hover:text-slate-500 hover:bg-slate-100"><Pencil className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
              {editId && consignment.some((o) => o.id === editId) && (
                <tr><td colSpan={6} className="px-4 pb-3 bg-slate-50/40">
                  <p className="text-xs text-slate-400 pt-2">Edit via the pencil on the main sheet after unticking consignment — or use the fields below.</p>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* THE TABLE — one calm sheet per step */}
      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400 border-b border-slate-100 bg-slate-50/60">
              {COLS[step].map((c, i) => {
                const sortable = !!sortKeys[c];
                const isCust = c === 'Distributor' || c === 'Customer';
                return (
                  <th key={i} className={clsx('px-3 py-2.5 font-semibold whitespace-nowrap', ['Cases', 'Value', 'Costs', 'Deductions'].includes(c) && 'text-right', ['Status', 'Freight Scheduled?', 'Invoice', 'Received'].includes(c) && 'text-center')}>
                    <button onClick={sortable ? () => toggleSort(c) : undefined}
                      className={clsx(sortable && 'hover:text-slate-600 cursor-pointer', sortCol === c && 'text-slate-700')}
                      title={sortable ? 'Sort' : undefined}>
                      {c}{sortCol === c ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </button>
                    {isCust && (
                      <span className="relative inline-block ml-1 align-middle">
                        <button onClick={(e) => { e.stopPropagation(); setShowCustMenu((s) => !s); }}
                          className={clsx(custFilter ? 'text-primary-600' : 'text-slate-300 hover:text-slate-500')}
                          title={custFilter ? `Filtered: ${custFilter}` : 'Filter'}>
                          <Filter className="w-3 h-3" />
                        </button>
                        {showCustMenu && (
                          <span className="absolute z-30 left-0 top-5 block bg-white rounded-xl shadow-lg border border-slate-100 py-1 min-w-[180px] max-h-64 overflow-y-auto font-normal normal-case text-slate-700">
                            <button onClick={() => { setCustFilter(''); setShowCustMenu(false); }} className={clsx('block w-full text-left px-3 py-1.5 hover:bg-slate-50', !custFilter && 'font-semibold text-primary-600')}>Everyone</button>
                            {custNames.map((n) => (
                              <button key={n} onClick={() => { setCustFilter(n); setShowCustMenu(false); }}
                                className={clsx('block w-full text-left px-3 py-1.5 hover:bg-slate-50', custFilter === n && 'font-semibold text-primary-600')}>{n}</button>
                            ))}
                          </span>
                        )}
                      </span>
                    )}
                  </th>
                );
              })}
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {visible.length === 0 && (
              <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-slate-400">Nothing here{custFilter ? ` for ${custFilter}` : ''}{needle ? ` matching “${q}”` : month !== 'all' ? ' this month' : ''}.{custFilter ? ' ' : ''}{custFilter && <button onClick={() => setCustFilter('')} className="text-primary-600 hover:underline">Show everyone</button>}</td></tr>
            )}
            {visible.map((o) => (
              <FragmentRow key={o.id} o={o} cells={rowCells(o)} colSpan={colSpan} editing={editId === o.id}
                form={form} setForm={setForm} saveEdit={saveEdit} cancelEdit={() => setEditId(null)} input={input}
                confirmDelete={confirmDelete} setConfirmDelete={setConfirmDelete} remove={remove} menuItems={menuItems} />
            ))}
          </tbody>
        </table>
      </div>

      {/* PO DETAIL — his page 3: SKU breakdown + BOL/PO, floating over the sheet */}
      {detailFor && (() => {
        const o = detailFor;
        const att = (o.attachments || []) as any[];
        const byKind = (k: string) => att.filter((f: any) => f.kind === k);
        return (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={() => setDetailFor(null)}>
            <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
              {/* the row, restated calmly on top */}
              <div className="bg-white rounded-t-2xl px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                <p className="text-sm"><span className="font-semibold text-slate-900">{o.poNumber || o.orderNumber}</span> <span className="text-slate-500">· {o.customer?.name}</span></p>
                <div className="flex items-center gap-3 text-sm text-slate-500">
                  <span>{Math.round((o.items || []).reduce((s: number, it: any) => s + (parseFloat(String(it.quantity)) || 0), 0)).toLocaleString('en-US')} cases</span>
                  <span className="font-semibold text-slate-900">{fmt(o.totalCents, o.currencyCode)}</span>
                  <button onClick={() => setDetailFor(null)} className="p-1.5 rounded-lg text-slate-300 hover:text-slate-500 hover:bg-slate-50"><X className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="bg-white px-5 pt-3 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                {/price est/i.test(o.notes || '') && <span className="text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded" title="No unit price in the source — valued at list price">price est.</span>}
                {o.shipFrom && <span>Ship from <b className="text-slate-700 font-medium">{o.shipFrom}</b></span>}
                {o.warehouse && <span>Warehouse <b className="text-slate-700 font-medium">{o.warehouse}</b></span>}
                {o.lotCode && o.lotCode !== 'na' && <span>Lot <b className="text-slate-700 font-medium">{o.lotCode}</b></span>}
                {o.shipDate && <span>Shipped <b className="text-slate-700 font-medium">{fmtDate(o.shipDate)}</b></span>}
                {o.broker && <span>Freight <b className="text-slate-700 font-medium">{o.broker}</b>{o.freightCents != null ? ` (${fmtSigned(o.freightCents)})` : ''}</span>}
                {o.notes && !/^(price est\.?|Direct delivery)/i.test(o.notes) && <span className="w-full text-slate-400">{o.notes}</span>}
              </div>
              <div className="bg-white rounded-b-2xl p-5 grid md:grid-cols-2 gap-4">
                {/* left: the SKU breakdown, exactly his layout */}
                <div className="rounded-2xl border border-slate-200 p-4">
                  <div className="space-y-2.5">
                    {(o.items || []).map((it: any) => (
                      <div key={it.id || it.name} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-baseline text-sm">
                        <span className="text-slate-700">{it.name}{it.lotCode && <span className="block text-[10px] text-slate-400">Lot {it.lotCode}</span>}</span>
                        <span className="text-slate-600 tabular-nums text-right">{Math.round(parseFloat(String(it.quantity)) || 0)}</span>
                        <span className="text-slate-500 tabular-nums text-right">${((it.unitPriceCents || 0) / 100).toFixed(0)}</span>
                        <span className="text-slate-700 tabular-nums text-right">${((it.totalPriceCents || 0) / 100).toLocaleString('en-US')}</span>
                      </div>
                    ))}
                    <div className="border-t border-slate-200 pt-2 grid grid-cols-[1fr_auto_auto_auto] gap-3 text-sm font-semibold">
                      <span />
                      <span className="tabular-nums text-right">{Math.round((o.items || []).reduce((s: number, it: any) => s + (parseFloat(String(it.quantity)) || 0), 0)).toLocaleString('en-US')}</span>
                      <span />
                      <span className="tabular-nums text-right text-slate-900">{fmt(o.totalCents, o.currencyCode)}</span>
                    </div>
                  </div>
                </div>
                {/* right: BOL / PO as his big buttons */}
                <div className="rounded-2xl border border-slate-200 p-4 flex flex-col items-center justify-center gap-4">
                  {(['bol', 'po'] as const).map((kind) => {
                    const files = byKind(kind);
                    return files.length > 0 ? (
                      <div key={kind} className="w-2/3">
                        {files.map((f: any) => (
                          <div key={f.id} className="relative group">
                            <button onClick={() => openFile(o.id, f)}
                              className="w-full py-3.5 rounded-xl bg-white border border-slate-200 shadow-sm text-base font-bold text-slate-800 hover:shadow-md hover:border-slate-300 transition"
                              title={`Open ${f.name}`}>
                              {kind.toUpperCase()}
                            </button>
                            <button onClick={() => removeFile.mutate({ id: o.id, fileId: f.id })}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-200 text-slate-500 hover:bg-red-100 hover:text-red-600 hidden group-hover:flex items-center justify-center"><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <label key={kind} className="w-2/3 cursor-pointer">
                        <span className="block w-full py-3.5 rounded-xl bg-white border border-dashed border-slate-300 shadow-sm text-base font-bold text-slate-400 hover:text-slate-600 hover:border-slate-400 transition text-center">
                          {upload.isPending ? '…' : `${kind.toUpperCase()} +`}
                        </span>
                        <input type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate({ id: o.id, file: f, kind }); e.target.value = ''; }} />
                      </label>
                    );
                  })}
                  {o.customer?.apptRequired && (
                    <p className="text-[11px] text-amber-700 flex items-start gap-1 px-2"><AlertCircle className="w-3 h-3 mt-0.5 shrink-0" /> Delivery appointment required{o.customer?.deliveryInstructions ? ` — ${o.customer.deliveryInstructions}` : ''}</p>
                  )}
                </div>
                {/* Per-delivery margin: items revenue − inventory item costs − this drop's
                    delivery cost. Blank parts say why — never a half-true number. */}
                {(() => {
                  const e = dropEconomics(o);
                  const cell = (v: number | null | undefined) =>
                    v == null ? <span className="text-slate-300">—</span> : <span className="tabular-nums">{fmt(v)}</span>;
                  return (
                    <div className="md:col-span-2 rounded-2xl border border-slate-200 p-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Per-delivery margin</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div><p className="text-xs text-slate-400">Items revenue</p><p className="font-semibold text-slate-900">{fmt(o.totalCents, o.currencyCode)}</p></div>
                        <div><p className="text-xs text-slate-400">− Item costs</p><p className="font-medium text-slate-700">{cell(e.itemCost)}</p>
                          {e.unmatched > 0 && <p className="text-[10px] text-amber-600">{e.unmatched} line{e.unmatched === 1 ? '' : 's'} not in Inventory</p>}</div>
                        <div><p className="text-xs text-slate-400">− Delivery cost</p><p className="font-medium text-slate-700">{cell(e.delivery)}</p>
                          {e.delivery == null && <p className="text-[10px] text-slate-400">add it via the order's pencil</p>}</div>
                        <div><p className="text-xs text-slate-400">= Margin</p>
                          <p className={clsx('font-semibold', e.margin == null ? 'text-slate-300' : e.margin >= 0 ? 'text-emerald-700' : 'text-rose-600')}>{e.margin == null ? '—' : fmt(e.margin)}</p></div>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-2">Item costs come from Inventory (unit cost, matched by product name/SKU); delivery cost is this order's recorded drop cost.</p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })()}

      {/* FILE PREVIEW — see the document first, download if wanted */}
      {preview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={closePreview}>
          <div className="bg-white rounded-2xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-slate-700 truncate">{preview.name}</p>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={downloadPreview} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition">
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
                <button onClick={closePreview} className="p-1.5 rounded-lg text-slate-300 hover:text-slate-500 hover:bg-slate-50"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-slate-100">
              {preview.isPdf ? (
                <iframe src={preview.url} title={preview.name} className="w-full h-full border-0" />
              ) : preview.isImage ? (
                <div className="w-full h-full overflow-auto flex items-start justify-center p-4">
                  <img src={preview.url} alt={preview.name} className="max-w-full rounded-lg shadow-sm" />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  No preview for this file type — use Download.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PICKED UP — gates Open → In Transit: the freight facts, required */}
      {pickupFor && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setPickupFor(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-semibold text-slate-900">Picked up — {pickupFor.poNumber || pickupFor.orderNumber}</h3>
              <p className="text-xs text-slate-500">{pickupFor.customer?.name} · moves to In Transit once the freight facts are in.</p>
            </div>
            <label className="block text-xs text-slate-500">Pickup date
              <input type="date" className={input} value={pickupForm.pickupDate} onChange={(e) => setPickupForm({ ...pickupForm, pickupDate: e.target.value })} />
            </label>
            <label className="block text-xs text-slate-500">Freight provider
              <input className={input} value={pickupForm.broker} onChange={(e) => setPickupForm({ ...pickupForm, broker: e.target.value })} placeholder="Warp, Central Transport, FOB…" />
            </label>
            <label className="block text-xs text-slate-500">Freight cost $ (0 for pickup / FOB)
              <input type="number" min="0" step="0.01" className={input} value={pickupForm.freightDollars} onChange={(e) => setPickupForm({ ...pickupForm, freightDollars: e.target.value })} />
            </label>
            <label className="text-xs text-slate-500 flex items-center gap-2">
              <input type="checkbox" checked={pickupForm.billedOnward} onChange={(e) => setPickupForm({ ...pickupForm, billedOnward: e.target.checked })} />
              Billed onward to the customer (not your cost)
            </label>
            <div className="flex gap-2 pt-1">
              <button onClick={confirmPickup}
                disabled={!pickupForm.pickupDate || !pickupForm.broker.trim() || pickupForm.freightDollars === ''}
                className="btn btn-primary">Move to In Transit</button>
              <button onClick={() => setPickupFor(null)} className="btn btn-ghost ml-auto">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* DELIVERED — gates In Transit → Delivered: just the date */}
      {deliverFor && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setDeliverFor(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-semibold text-slate-900">Delivered — {deliverFor.poNumber || deliverFor.orderNumber}</h3>
              <p className="text-xs text-slate-500">{deliverFor.customer?.name} · this date is when the sale counts.</p>
            </div>
            <label className="block text-xs text-slate-500">Delivered on
              <input type="date" className={input} value={deliverDate} onChange={(e) => setDeliverDate(e.target.value)} />
            </label>
            <div className="flex gap-2 pt-1">
              <button onClick={confirmDeliver} disabled={!deliverDate} className="btn btn-primary">Move to Delivered</button>
              <button onClick={() => setDeliverFor(null)} className="btn btn-ghost ml-auto">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* SEND INVOICE — the actionable step */}
      {invoiceFor && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setInvoiceFor(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-semibold text-slate-900">Send invoice — {invoiceFor.poNumber || invoiceFor.orderNumber}</h3>
              <p className="text-xs text-slate-500">{invoiceFor.customer?.name} · {fmt(invoiceFor.totalCents, invoiceFor.currencyCode)}{invoiceFor.customer?.paymentTerms ? ` · ${invoiceFor.customer.paymentTerms}` : ''}</p>
            </div>
            {invoiceFor.customer?.deliveryInstructions && (
              <p className="text-[11px] text-slate-500 bg-slate-50 rounded-lg px-2.5 py-1.5">{invoiceFor.customer.deliveryInstructions}</p>
            )}
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-slate-500">Invoice #
                <input className={input} value={invForm.invoiceNumber} onChange={(e) => setInvForm({ ...invForm, invoiceNumber: e.target.value })} placeholder="e.g. INV-0083" />
              </label>
              <label className="block text-xs text-slate-500">Sent date
                <input type="date" className={input} value={invForm.sentAt} onChange={(e) => setInvForm({ ...invForm, sentAt: e.target.value })} />
              </label>
            </div>
            <label className="block text-xs text-slate-500">To (their invoicing address — from the customer profile)
              <input className={input} value={invForm.to} onChange={(e) => setInvForm({ ...invForm, to: e.target.value })} placeholder="ap@distributor.com, second@…" />
            </label>
            <label className="block text-xs text-slate-500">Email text (their required wording, PO filled in)
              <textarea className={clsx(input, 'min-h-[70px]')} value={invForm.bodyText} onChange={(e) => setInvForm({ ...invForm, bodyText: e.target.value })} />
            </label>
            <label className="block text-xs text-slate-500">Due date (blank = from their terms)
              <input type="date" className={input} value={invForm.dueAt} onChange={(e) => setInvForm({ ...invForm, dueAt: e.target.value })} />
            </label>
            <div className="flex items-center gap-2 pt-1">
              <button onClick={() => sendInvoice.mutate({ id: invoiceFor.id, body: { ...invForm, invoiceNumber: invForm.invoiceNumber || undefined, dueAt: invForm.dueAt || undefined, sentAt: invForm.sentAt || undefined } })}
                disabled={sendInvoice.isPending || !invForm.to.includes('@') || !invForm.invoiceNumber.trim() || !invForm.sentAt} className="btn btn-primary">
                {sendInvoice.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send email'}
              </button>
              <button onClick={() => sendInvoice.mutate({ id: invoiceFor.id, body: { invoiceNumber: invForm.invoiceNumber || undefined, dueAt: invForm.dueAt || undefined, sentAt: invForm.sentAt || undefined, markOnly: true } })}
                disabled={sendInvoice.isPending || !invForm.invoiceNumber.trim() || !invForm.sentAt} className="btn btn-secondary" title="For customers invoiced elsewhere, e.g. directly in QuickBooks">
                Mark as sent
              </button>
              <button onClick={() => setInvoiceFor(null)} className="btn btn-ghost ml-auto">Cancel</button>
            </div>
            <p className="text-[11px] text-slate-400">Invoice # and sent date are required — that's what this stage records.</p>
          </div>
        </div>
      )}

      {/* RECEIVED — payment + deductions, then closed */}
      {receiveFor && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setReceiveFor(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-semibold text-slate-900">Payment received — {receiveFor.poNumber || receiveFor.orderNumber}</h3>
              <p className="text-xs text-slate-500">{receiveFor.customer?.name} · invoiced {fmt(invByOrder.get(receiveFor.id)?.totalCents ?? receiveFor.totalCents, receiveFor.currencyCode)}</p>
            </div>
            <label className="block text-xs text-slate-500">Amount received $
              <input type="number" step="0.01" className={input} value={recvForm.amount} onChange={(e) => setRecvForm({ ...recvForm, amount: e.target.value })} />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs text-slate-500">Date received
                <input type="date" className={input} value={recvForm.receivedAt} onChange={(e) => setRecvForm({ ...recvForm, receivedAt: e.target.value })} />
              </label>
              <label className="block text-xs text-slate-500">Method
                <select className={input} value={recvForm.method} onChange={(e) => setRecvForm({ ...recvForm, method: e.target.value })}>
                  <option value="ach">ACH</option><option value="check">Check</option><option value="wire">Wire</option><option value="other">Other</option>
                </select>
              </label>
            </div>
            {(() => {
              const inv = invByOrder.get(receiveFor.id);
              const cents = Math.round(parseFloat(recvForm.amount || '0') * 100) || 0;
              const short = inv ? Math.max(0, (inv.totalCents - inv.amountPaidCents) - cents) : 0;
              if (short <= 0) return null;
              return (
                <label className="block text-xs text-amber-700">Short {fmt(short, receiveFor.currencyCode)} — why?
                  <select className={input} value={recvForm.reason} onChange={(e) => setRecvForm({ ...recvForm, reason: e.target.value })}>
                    <option value="">Pick a reason…</option>
                    {['promo', 'advertising', 'slotting', 'samples', 'chargeback', 'freight', 'shortpay', 'out_of_code', 'spoilage', 'other'].map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                  </select>
                  <input className={input + ' mt-1.5'} placeholder="Note — e.g. which report/line it's from (optional, but future-you says thanks)"
                    value={recvForm.note} onChange={(e) => setRecvForm({ ...recvForm, note: e.target.value })} />
                </label>
              );
            })()}
            <div className="flex gap-2">
              <button onClick={() => receive.mutate({ order: receiveFor, cents: Math.round(parseFloat(recvForm.amount || '0') * 100) || 0, reason: recvForm.reason, note: recvForm.note, receivedAt: recvForm.receivedAt, method: recvForm.method })}
                disabled={receive.isPending || !recvForm.receivedAt || recvForm.amount === ''} className="btn btn-primary">{receive.isPending ? 'Saving…' : 'Record & close'}</button>
              <button onClick={() => setReceiveFor(null)} className="btn btn-ghost ml-auto">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// A table row + its optional expansion/editor rows. Kept separate so the main
// map stays readable.
function FragmentRow({ o, cells, colSpan, editing, form, setForm, saveEdit, cancelEdit, input, confirmDelete, setConfirmDelete, remove, menuItems = [] }: any) {
  return (
    <>
      <tr className="hover:bg-slate-50/50">{cells}</tr>
      {editing && (
        <tr>
          <td colSpan={colSpan} className="px-4 pb-3 bg-slate-50/40">
            <div className="rounded-xl bg-white border border-slate-100 p-3 grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-2">
              <label className="text-xs text-slate-500">PO #<input className={input} value={form.poNumber} onChange={(e) => setForm({ ...form, poNumber: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Freight provider<input className={input} value={form.broker} onChange={(e) => setForm({ ...form, broker: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Freight $ (− you pay, + billed on)<input type="number" className={input} value={form.freightDollars} onChange={(e) => setForm({ ...form, freightDollars: e.target.value })} /></label>
              <label className="text-xs text-slate-500" title="What this drop costs you to deliver — powers the per-delivery margin on the PO detail">Delivery cost $ / drop<input type="number" min="0" step="0.01" className={input} value={form.deliveryDollars} onChange={(e) => setForm({ ...form, deliveryDollars: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Ship from<input className={input} value={form.shipFrom} onChange={(e) => setForm({ ...form, shipFrom: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Warehouse<input className={input} value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Lot code<input className={input} value={form.lotCode} onChange={(e) => setForm({ ...form, lotCode: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Order date<input type="date" className={input} value={form.fulfillmentDate} onChange={(e) => setForm({ ...form, fulfillmentDate: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Ship date<input type="date" className={input} value={form.shipDate} onChange={(e) => setForm({ ...form, shipDate: e.target.value })} /></label>
              <label className="text-xs text-slate-500">Delivery date<input type="date" className={input} value={form.deliveryDate} onChange={(e) => setForm({ ...form, deliveryDate: e.target.value })} /></label>
              <div className="col-span-2 sm:col-span-3 space-y-1.5">
                <div className="flex items-center gap-2 text-[10px] font-medium text-slate-400 uppercase tracking-wide">
                  <span className="flex-1">SKU</span><span className="w-20">Cases</span><span className="w-4" /><span className="w-24">$ / case</span><span className="w-24">Lot code</span>
                </div>
                {(form.items || []).map((l: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    {menuItems.length > 0 ? (
                      <select className="flex-1 min-w-0 text-sm rounded-lg border border-slate-200 px-2 py-1" value={l.menuItemId || ''}
                        onChange={(e) => { const r = [...form.items]; const m = menuItems.find((x: any) => x.id === e.target.value); r[i] = { ...r[i], menuItemId: e.target.value, name: m?.name || r[i].name }; setForm({ ...form, items: r }); }}>
                        <option value="">Pick the SKU…</option>
                        {menuItems.map((m: any) => <option key={m.id} value={m.id}>{skuLabel(m)}</option>)}
                      </select>
                    ) : <span className="flex-1 text-slate-600 truncate">{l.name}</span>}
                    <input type="number" min="0" className="w-20 text-sm rounded-lg border border-slate-200 px-2 py-1" value={l.quantity || ''}
                      onChange={(e) => { const r = [...form.items]; r[i] = { ...r[i], quantity: parseFloat(e.target.value) || 0 }; setForm({ ...form, items: r }); }} />
                    <span className="text-slate-400">×</span>
                    <input type="number" min="0" step="0.01" className="w-24 text-sm rounded-lg border border-slate-200 px-2 py-1" value={l.unitDollars}
                      onChange={(e) => { const r = [...form.items]; r[i] = { ...r[i], unitDollars: e.target.value }; setForm({ ...form, items: r }); }} />
                    <input className="w-24 text-sm rounded-lg border border-slate-200 px-2 py-1" placeholder="—" value={l.lotCode || ''}
                      onChange={(e) => { const r = [...form.items]; r[i] = { ...r[i], lotCode: e.target.value }; setForm({ ...form, items: r }); }} />
                  </div>
                ))}
              </div>
              <label className="text-xs text-slate-500 flex items-center gap-2 mt-5"><input type="checkbox" checked={form.onConsignment} onChange={(e) => setForm({ ...form, onConsignment: e.target.checked })} /> On consignment</label>
              <div className="col-span-2 sm:col-span-3 flex gap-2 pt-1">
                <button onClick={() => saveEdit(o.id)} className="btn btn-sm btn-primary flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Save</button>
                <button onClick={cancelEdit} className="btn btn-sm btn-secondary flex items-center gap-1"><X className="w-3.5 h-3.5" /> Cancel</button>
              </div>
            </div>
          </td>
        </tr>
      )}
      {confirmDelete === o.id && (
        <tr>
          <td colSpan={colSpan} className="px-4 pb-3 bg-rose-50/50">
            <div className="flex items-center gap-3 pt-2 text-sm text-rose-800">
              Delete {o.poNumber || o.orderNumber}? This can't be undone.
              <button onClick={() => remove.mutate(o.id)} className="btn btn-sm bg-rose-600 text-white hover:bg-rose-700 rounded-lg px-3 py-1">Delete</button>
              <button onClick={() => setConfirmDelete(null)} className="btn btn-sm btn-ghost">Cancel</button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
